import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, Crown, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Pricing() {
  const [packages, setPackages] = useState([]);
  const [loadingId, setLoadingId] = useState(null);
  const { me } = useAuth();

  useEffect(() => {
    axios
      .get(`${API}/packages`)
      .then((r) => setPackages(r.data))
      .catch(() => toast.error("Could not load packages"));
  }, []);

  const handleBuy = async (pkg) => {
    if (!(pkg.stripe_link && pkg.stripe_link.trim())) {
      toast.error("Buy link not set up for this pack yet — please ping Rumple on Discord.");
      return;
    }
    // Auto-grant requires the buyer to be signed in (so we can attribute the purchase).
    if (!me) {
      toast.error("Please sign in with Discord first so your pack unlocks automatically after checkout.");
      // Bounce to Discord login
      try {
        const r = await axios.get(`${API}/auth/discord/login`);
        if (r.data?.authorization_url) window.location.href = r.data.authorization_url;
      } catch {
        /* user can manually click Sign In */
      }
      return;
    }
    const url = new URL(pkg.stripe_link.trim());
    url.searchParams.set("client_reference_id", `${me.id}::${pkg.id}`);
    if (me.email) url.searchParams.set("prefilled_email", me.email);
    window.location.href = url.toString();
  };

  const standard = packages.filter((p) => p.tier === "standard");
  const vip = packages.find((p) => p.tier === "vip");

  return (
    <section id="pricing" className="relative py-24 md:py-32" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10">
        <div className="max-w-2xl">
          <div className="section-label" data-testid="pricing-label">Pricing</div>
          <h2 className="font-display font-black uppercase mt-3 text-4xl md:text-5xl leading-tight" data-testid="pricing-title">
            Pick your <span style={{ color: "var(--rv-red)" }}>edge</span>.
          </h2>
          <p className="text-white/65 mt-4 text-base md:text-lg leading-relaxed">
            Every pack is plug-and-play on Cronus Zen — load the GPC, follow the guide, ranked-ready in minutes.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
          {standard.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className={`rv-card rounded-xl p-7 flex flex-col relative overflow-hidden ${p.on_sale ? "rv-card-sale" : ""}`}
              data-testid={`pkg-card-${p.id}`}
            >
              {p.on_sale && <div className="rv-sale-ribbon" data-testid={`sale-ribbon-${p.id}`}>SALE</div>}
              {p.is_new && <div className="rv-new-badge" data-testid={`new-badge-${p.id}`}>NEW</div>}
              <div className="flex items-center justify-between">
                <h3 className="font-display font-bold uppercase text-xl tracking-tight">{p.name}</h3>
                <Sparkles size={16} style={{ color: "var(--rv-red)" }} />
              </div>
              <p className="text-white/55 text-sm mt-1">{p.tagline}</p>

              <div className="my-6 flex items-baseline gap-1">
                <span className="font-display font-black text-5xl">${p.price.toFixed(2)}</span>
                <span className="text-white/40 text-sm">USD</span>
              </div>

              <ul className="space-y-2.5 text-sm text-white/80 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex gap-2 items-start">
                    <Check size={16} className="mt-0.5 shrink-0" style={{ color: "var(--rv-red)" }} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleBuy(p.id)}
                disabled={loadingId === p.id}
                className="btn-primary justify-center w-full mt-6"
                data-testid={`buy-btn-${p.id}`}
              >
                {loadingId === p.id ? <Loader2 size={16} className="animate-spin" /> : "Buy Now"}
              </button>
            </motion.div>
          ))}
        </div>

        {vip && (
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className={`rv-card-vip rounded-2xl p-8 md:p-12 mt-10 grid lg:grid-cols-[1.4fr_1fr] gap-10 items-center relative overflow-hidden ${vip.on_sale ? "rv-card-sale" : ""}`}
            data-testid="vip-card"
          >
            {vip.on_sale && <div className="rv-sale-ribbon" data-testid="sale-ribbon-vip">SALE</div>}
            {vip.is_new && <div className="rv-new-badge" data-testid="new-badge-vip">NEW</div>}
            <div
              className="absolute -top-24 -right-20 w-80 h-80 rounded-full blur-3xl opacity-50"
              style={{ background: "radial-gradient(circle, rgba(212,175,55,0.5), transparent 65%)" }}
            />
            <div className="relative">
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-yellow-500/40 bg-yellow-500/10"
                data-testid="vip-badge"
              >
                <Crown size={14} style={{ color: "var(--rv-gold)" }} />
                <span className="text-[10px] uppercase tracking-[0.3em] font-bold" style={{ color: "var(--rv-gold)" }}>
                  Best Value · VIP
                </span>
              </div>
              <h3 className="font-display font-black uppercase mt-5 text-4xl md:text-5xl leading-tight">
                {vip.name}
              </h3>
              <p className="text-white/70 mt-3 text-base md:text-lg">{vip.tagline}</p>

              <ul className="mt-7 grid sm:grid-cols-2 gap-y-3 gap-x-6 text-sm text-white/85">
                {vip.features.map((f) => (
                  <li key={f} className="flex gap-2 items-start">
                    <Check size={16} className="mt-0.5 shrink-0" style={{ color: "var(--rv-gold)" }} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative flex flex-col items-start lg:items-end">
              <div className="flex items-baseline gap-1">
                <span className="font-display font-black text-6xl md:text-7xl" style={{ color: "var(--rv-gold)" }}>
                  ${vip.price.toFixed(2)}
                </span>
                <span className="text-white/50 text-sm">USD</span>
              </div>
              <div className="text-xs uppercase tracking-widest text-white/55 mt-1">One-time · Lifetime updates</div>
              <button
                onClick={() => handleBuy(vip)}
                disabled={loadingId === vip.id}
                className="btn-gold mt-8"
                data-testid={`buy-btn-${vip.id}`}
              >
                {loadingId === vip.id ? <Loader2 size={16} className="animate-spin" /> : "Unlock VIP"}
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
}
